import { useEffect, useRef } from "react";
import { isTypingInForm } from "../../../utils/builderInputUtils";

const DEFAULT_OFFSET = 140;

/**
 * Returns the section that matches the sticky header offset line.
 * Activates a step as soon as its top edge reaches the offset (enters the viewport band).
 */
export const getActiveSectionId = (sectionIds, sectionRefs, offset = DEFAULT_OFFSET) => {
  const sections = sectionIds
    .map((id) => {
      const el = sectionRefs.current[id];
      if (!el?.isConnected) return null;
      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) return null;
      return { id, top: rect.top, bottom: rect.bottom };
    })
    .filter(Boolean);

  if (!sections.length) {
    return sectionIds[sectionIds.length - 1] || sectionIds[0];
  }

  const anchor = offset;

  const atBottom =
    window.innerHeight + window.scrollY >=
    document.documentElement.scrollHeight - 2;
  if (atBottom) {
    return sections[sections.length - 1].id;
  }

  const straddlingAnchor = sections.filter(
    ({ top, bottom }) => top <= anchor && bottom > anchor
  );
  if (straddlingAnchor.length) {
    return straddlingAnchor[straddlingAnchor.length - 1].id;
  }

  let activeId = sections[0].id;
  for (const section of sections) {
    if (section.top <= anchor) {
      activeId = section.id;
    }
  }

  return activeId;
};

export const usePackageSectionSpy = ({
  sectionIds,
  sectionRefs,
  isScrollingRef,
  pinnedSectionRef,
  editingSectionRef,
  isSubmittingRef,
  onActiveChange,
  offset = DEFAULT_OFFSET,
  enabled = true,
  scrollRoot = null,
}) => {
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  useEffect(() => {
    if (!enabled) return undefined;

    const scrollTarget = scrollRoot || window;
    let debounceTimer = null;

    const handleScroll = () => {
      if (isScrollingRef?.current || pinnedSectionRef?.current) return;
      if (editingSectionRef?.current) return;
      if (isSubmittingRef?.current) return;
      if (isTypingInForm()) return;

      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        if (isScrollingRef?.current || pinnedSectionRef?.current) return;
        if (editingSectionRef?.current) return;
        if (isSubmittingRef?.current) return;
        if (isTypingInForm()) return;
        const activeId = getActiveSectionId(sectionIds, sectionRefs, offset);
        onActiveChangeRef.current(activeId);
      }, 40);
    };

    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    const syncTimers = [500, 1500].map((delay) =>
      window.setTimeout(handleScroll, delay)
    );

    return () => {
      window.clearTimeout(debounceTimer);
      syncTimers.forEach((timerId) => window.clearTimeout(timerId));
      scrollTarget.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [
    sectionIds,
    sectionRefs,
    isScrollingRef,
    pinnedSectionRef,
    editingSectionRef,
    isSubmittingRef,
    offset,
    enabled,
    scrollRoot,
  ]);
};
